import {
  FetchImplementation,
  HttpResource,
  Link,
  NumberRange,
} from "@readium/shared";

import {
  addBreadcrumb,
  captureException,
  captureMessage,
} from "@sentry/browser";

import { parseDynamicImport } from "../../modules/shared-utils/vite.preload";

import { render } from "./injected";
import { checkMarkup } from "./markupSanity";

function getEpubEntryPoint(): URL {
  return parseDynamicImport(
    () => import("../../modules/epub-iframe"),
    import.meta
  ).url;
}

function getCustomFontsCss(): URL | "" {
  try {
    return parseDynamicImport(() => import("unfonts.css"), import.meta).url;
  } catch (e) {
    // no hard fail for custom fonts, just log the error and return an empty string
    captureException(e);
    return "";
  }
}

type SerializedError = {
  name: string;
  message: string;
  stack?: string;
  cause?: SerializedError;
};

function deserializeError(error?: SerializedError): Error | null {
  if (!error) {
    return null;
  }

  // error got sent successfully through postMessage
  if (error instanceof Error) {
    return error;
  }

  // structured clone failed, reconstruct the error from its serialized form
  const obj = new Error(error?.message ?? "Unknown error");
  if (error?.name) {
    obj.name = error.name;
  }
  if (error?.stack) {
    obj.stack = error.stack;
  }
  if (error?.cause) {
    obj.cause = deserializeError(error.cause);
  }
  return obj;
}

window.addEventListener("message", function (event) {
  const type = event.data?.type;
  if (typeof type !== "string") {
    return;
  }

  // TODO: eventually do something with the success message
  if (type === "dm-inject-error") {
    const exception =
      deserializeError(event.data.error?.error ?? event.data.error) ??
      new Error("Unknown error");
    captureException(exception, {
      extra: event.data.error,
    });
  }
});

export class HtmlResource extends HttpResource {
  private static _entryPoint: URL | undefined = undefined;
  private static get entryPoint(): URL {
    return (this._entryPoint ??= getEpubEntryPoint());
  }

  private static _customFontsCss: URL | "" | undefined = undefined;
  private static get customFontsCss(): URL | undefined {
    if (import.meta.env.DEV) {
      // don't inject custom fonts in dev mode, let vite's client do it
      // this avoids a duplicate injection of the same css file
      // in DEV, the CSS file is imported from javascript and handled by vite's client,
      // this takes care of HMR as well
      return undefined;
    }
    return (this._customFontsCss ??= getCustomFontsCss()) || undefined;
  }

  private readonly href: string;

  constructor(fetch: FetchImplementation, link: Link, url: string) {
    super(fetch, link, url);
    this.href = link.href;
  }
  async read(range?: NumberRange): Promise<Uint8Array | undefined> {
    if (range) {
      throw new Error("read not implemented.");
    }
    const text = await this.readAsString();
    return new TextEncoder().encode(text);
  }
  async length(): Promise<number> {
    return (await this.read())?.length ?? 0;
  }
  readAsJSON(): Promise<unknown> {
    throw new Error("readAsJSON not supported for HTML");
  }
  async readAsString(): Promise<string> {
    let doc = await super.readAsString();

    const sanity = checkMarkup(doc);
    doc = sanity.doc;
    if (sanity.repairedHead) {
      addBreadcrumb({
        category: "markup-sanity",
        message: "inserted missing <head>",
        data: { href: this.href },
      });
    }
    if (sanity.anomalies.length) {
      captureMessage("HTML resource failed markup sanity checks", {
        extra: { href: this.href, anomalies: sanity.anomalies },
      });
    }

    let injected = false;
    const inject = (append: string = "") => {
      const scripts = import.meta.env.DEV
        ? [new URL("/@vite/client", document.baseURI), HtmlResource.entryPoint]
        : [HtmlResource.entryPoint];
      return (
        render(scripts, window.origin, HtmlResource.customFontsCss) + append
      );
    };

    const replacer = (match: string) => {
      if (injected) {
        return match;
      }
      injected = true;
      return inject(match);
    };
    // before closing head or body
    doc = doc.replace(/<\/head>/i, replacer);
    if (!injected) {
      doc = doc.replace(/<\/body>/i, replacer);
    }

    // fallback to before closing html
    if (!injected) {
      doc = doc.replace(/<\/html>/i, replacer);
    }

    // last resort, append at the end of the document
    if (!injected) {
      doc += inject();
    }

    return doc;
  }
  readAsXML(): Promise<XMLDocument> {
    throw new Error("readAsXML not implemented.");
  }
}
